/**
 * PersonnelSection — slim composed Personnel on Duty card.
 *
 * This is the refactored, modular replacement for the legacy
 * PersonnelCard.tsx (which is still untouched). When you're ready to
 * verify, swap the import in DesktopLayout from:
 *
 *   import PersonnelCard from '../components/personnel/PersonnelCard'
 *
 * to:
 *
 *   import PersonnelSection from '../components/personnel/PersonnelSection'
 *
 * Built entirely from the granular pieces extracted in Tier 3:
 *   - PersonnelHeader  (title + Reset Shift + Desk Officer)
 *   - OfficerCard      (single slot, gold or silver)
 *   - personnelHelpers (SUPERVISOR_SPLIT, etc.)
 *
 * The PersonnelSection itself only handles layout and the summary
 * footer counts. All the heavy lifting lives in its children.
 */
import { useDesktopStore } from '../../stores/desktop'
import { SUPERVISOR_SPLIT } from './personnelHelpers'
import PersonnelHeader from './PersonnelHeader'
import OfficerCard from './OfficerCard'

// All roles that render as supervisor cards (gold badge, wider grid)
const SUP_ROLES_SET = new Set([
  'Dir/AD/Lt', 'Sergeant', 'OIC', 'Lieutenant',
  'Director of Security', 'Assistant Director', 'Director', 'Developer',
])

export default function PersonnelSection() {
  const personnel = useDesktopStore(s => s.personnel)

  // Role-based split — Hanson (Lt) stays in supervisor section even if
  // normalizePersonnel placed her at index 3+ due to slot overflow.
  // We carry the actual personnel array index alongside each entry so
  // OfficerCard calls updateOfficer(actualIndex) not the filtered index.
  type IndexedEntry = { entry: typeof personnel[0]; actualIndex: number }
  const supervisors: IndexedEntry[] = []
  const officers: IndexedEntry[] = []
  personnel.forEach(function(entry, i) {
    if (SUP_ROLES_SET.has(entry.role) || (i < SUPERVISOR_SPLIT && !entry.name)) {
      supervisors.push({ entry, actualIndex: i })
    } else {
      officers.push({ entry, actualIndex: i })
    }
  })

  // Rank order for packing on-duty supervisors to the front of the grid.
  // Lower number = higher rank = leftmost. Empty placeholder slots sort last
  // so the row reads e.g. Director · Asst. Director · Sergeant with no empty
  // gap in the middle, and a 4th supervisor simply wraps to the next row.
  const SUP_RANK: Record<string, number> = {
    'Developer': 0,
    'Director of Security': 1, 'Director': 1,
    'Assistant Director': 2,
    'Lieutenant': 3,
    'Dir/AD/Lt': 4,        // generic/derived supervisor role
    'Sergeants': 5, 'Sergeant': 5,
    'OIC': 6,
  }
  function supRank(e: typeof personnel[0]): number {
    if (!e.name) return 100 // empty placeholders always trail
    return SUP_RANK[e.role] ?? 50
  }
  supervisors.sort((a, b) => {
    const ra = supRank(a.entry), rb = supRank(b.entry)
    if (ra !== rb) return ra - rb
    // tie-break by call sign number so S-01 precedes S-02
    const na = parseInt((a.entry.callSign || '').replace(/\D/g, '')) || 999
    const nb = parseInt((b.entry.callSign || '').replace(/\D/g, '')) || 999
    return na - nb
  })

  // Supervisor visibility rule:
  //   - Always show at least 3 cards: named supervisors pack to the front
  //     (rank-sorted), empty DEFAULT slots pad the row out to 3.
  //   - Once 3+ are filled, the leftover empty defaults are hidden (no stray
  //     placeholder past a full row of 3).
  //   - Slots explicitly added via the Add Supervisor button (manualAdd) ALWAYS
  //     show, even beyond 3, so a 4th can be assigned.
  const namedSups   = supervisors.filter(s => s.entry.name)
  const addedEmpties = supervisors.filter(s => !s.entry.name && s.entry.manualAdd)
  const defaultEmpties = supervisors.filter(s => !s.entry.name && !s.entry.manualAdd)
  const MIN_SUP_SLOTS = 3
  const padNeeded = Math.max(0, MIN_SUP_SLOTS - namedSups.length)
  const visibleSupervisors = [
    ...namedSups,
    ...defaultEmpties.slice(0, padNeeded), // pad to 3, hidden once 3 filled
    ...addedEmpties,                       // explicit additions always show
  ]

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 14,
      }}
    >
      <PersonnelHeader />

      {/* Supervisor row — standard 3-per-row grid. On-duty supervisors pack to
          the front in rank order (Director → AD → Lt → Sergeant → OIC); empty
          default-label placeholders trail. A 4th supervisor wraps to the next
          row, bottom-left — it does NOT stretch the row. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(290px, 1fr))',
          gap: 10,
          marginBottom: 10,
        }}
      >
        {visibleSupervisors.map(({ entry, actualIndex }) => (
          <OfficerCard key={actualIndex} entry={entry} index={actualIndex} isSup />
        ))}
      </div>

      {/* Officer grid — 3 cols, smaller badges.
          minmax(266px, 1fr) = 250px flex content + 16px card padding. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(266px, 1fr))', gap: 8 }}>
        {officers.map(({ entry, actualIndex }) => (
          <OfficerCard
            key={actualIndex}
            entry={entry}
            index={actualIndex}
            isSup={false}
          />
        ))}
      </div>

      {/* Summary footer */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid var(--border)',
          fontSize: 11,
          color: 'var(--text-muted)',
        }}
      >
        <span>
          Total:{' '}
          <strong style={{ color: 'var(--accent)' }}>
            {personnel.filter(p => p.name).length}
          </strong>
        </span>
        <span>
          Supervisors:{' '}
          <strong style={{ color: '#3b82f6' }}>
            {supervisors.filter(({ entry: p }) => p.name).length}
          </strong>
        </span>
        <span>
          Officers:{' '}
          <strong style={{ color: '#22c55e' }}>
            {officers.filter(({ entry: p }) => p.name).length}
          </strong>
        </span>
      </div>
    </div>
  )
}
